import React, { useEffect , useState } from "react";
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

import "./HomePage.css";

import { getUserInfo } from "../../services/User.js";
import { API_BASE_URL } from "../../Variables";
import BookDisplay from "../../components/BookDisplay/BookDisplay.js";
import BookDetailsModal from "../../components/BookDetailsModal/BookDetailsModal.js";

function HomePage() {
  // State for User and Recommendations
  const user = getUserInfo();
  const displayRecommendations = user.recommendations;
  const token = localStorage.getItem("token");

  const [genrePreferences, setGenrePreferences] = useState([]);
  const [recommendationsBooks, setRecommendationsBooks] = useState({});
  const [recommendationsError, setRecommendationsError] = useState(null);
  const [preferenceFlag, setPreferenceFlag] = useState(false);

  // State for General Genres and Recommendations
  const [randomGenres, setRandomGenres] = useState([]);
  const [genreBooks, setGenreBooks] = useState({});
  const [loadingStates, setLoadingStates] = useState({});
  const [errorMessages, setErrorMessages] = useState({});
  const [genreError, setGenreError] = useState(null);

  // Book Details Modal State
  const [displayBookDetailsModal, setDisplayBookDetailsModal] = useState(false);
  const [selectedBookId, setSelectedBookId] = useState(null);

  const navigate = useNavigate();
  const limit = 10; // Limit for fetched books

  const handleBookClick = (bookId) => {
    setSelectedBookId(bookId);
    setDisplayBookDetailsModal(true);
  };

  // Function to handle click on view all button
  const handleListViewClick = (genre) => {
    return () => {
      navigate(`/home/${genre}`);
    };
  };

  // Function to fetch books for a genre
  const fetchBooksForGenre = async (genre, maxResults = limit, controller) => {
    try {

      let query = genre.toLowerCase();

      const params = {
        q: query,
        startIndex: 0,
        maxResults: maxResults,
      };

      const response = await axios.get(`${API_BASE_URL}/books-api/`, {
        params
      });

      return response.data.items;
    } catch (error) {
      if (error.name !== 'CanceledError' && error.response.data.message) {
        setErrorMessages((prevErrors) => ({
          ...prevErrors,
          [genre]: error.response.data.message + '. Please try again later.',
        }));
      } else if(error.name !== 'CanceledError') {
        setErrorMessages((prevErrors) => ({
          ...prevErrors,
          [genre]: 'Failed to fetch books. Please try again later.',
        }));
      }
      return [];
    }
  };

  // Fetch books for random genres once genres are fetched
  useEffect(() => {
    if (randomGenres.length > 0) {
      const controller = new AbortController();
      const fetchGenreBooks = async () => {
        const genreBooksData = {};
        const newLoadingStates = {};
        const newErrorMessages = {};

        randomGenres.forEach((genre) => {
          const genreDescription = genre.description;
          newLoadingStates[genreDescription] = true;
          newErrorMessages[genreDescription] = null;
        });

        setLoadingStates((prevLoading) => ({
          ...prevLoading,
          ...newLoadingStates,
        }));
        setErrorMessages((prevErrors) => ({
          ...prevErrors,
          ...newErrorMessages,
        }));

        try {
          const booksPromises = randomGenres.map(async (genre) => {
            const genreDescription = genre.description;
            const books = await fetchBooksForGenre(genreDescription, limit, controller);
            return { genreDescription, books };
          });

          const genresWithBooks = await Promise.all(booksPromises);

          genresWithBooks.forEach(({ genreDescription, books }) => {
            genreBooksData[genreDescription] = books;
          });  

          setGenreBooks(genreBooksData);
          setLoadingStates((prevLoading) => randomGenres.reduce((acc, genre) => {
            acc[genre.description] = false;
            return acc;
          }, { ...prevLoading }));
        } catch (error) {
          // Errors handled in fetchBooksForGenre()
        }
      };

      fetchGenreBooks();

      return () => controller.abort(); // Cleanup function
    }
  }, [randomGenres]);

  // Fetch genre preferences and recommendations
  useEffect(() => {
    if (displayRecommendations && genrePreferences.length === 0) {
      const controller = new AbortController(); // Create an AbortController
      const fetchGenrePreferences = async () => {
        try {
          const response = await axios.get(`${API_BASE_URL}/genre-preferences/top`, {
            headers: { 'Authorization': `Bearer ${token}` },
            params: { limit: 2 },
            signal: controller.signal // Pass AbortController signal
          });
          setGenrePreferences(response.data);
          setPreferenceFlag(true);
        } catch (error) {
          if (error.name !== 'CanceledError') {
            setRecommendationsError("Failed to load genre preferences. Please try again later.");
          }
        }
      };

      fetchGenrePreferences();

      return () => controller.abort(); // Cleanup function
    }
  }, [displayRecommendations, token, genrePreferences.length]);

  // Fetch recommendations for genre preferences
  useEffect(() => {
    const controller = new AbortController(); // Create an AbortController

    // If genre preferences are available and recommendations are enabled, fetch recommendations
    if (displayRecommendations && genrePreferences.length > 0) {
      const fetchGenreBooks = async () => {
        const genreBooksData = {};
        const newLoadingStates = {};
        const newErrorMessages = {};

        // Set loading states and error messages for each genre
        genrePreferences.forEach(({ Genre }) => {
          const genreDescription = Genre.description;
          newLoadingStates[genreDescription] = true;
          newErrorMessages[genreDescription] = null;
        });

        setLoadingStates((prevLoading) => ({
          ...prevLoading,
          ...newLoadingStates,
        }));
        setErrorMessages((prevErrors) => ({
          ...prevErrors,
          ...newErrorMessages,
        }));

        // Fetch books for each genre
        try {
          const booksPromises = genrePreferences.map(async ({ Genre }) => {
            const genreDescription = Genre.description;
            const books = await fetchBooksForGenre(genreDescription, limit, controller);
            return { genreDescription, books };
          });

          const genresWithBooks = await Promise.all(booksPromises);

          genresWithBooks.forEach(({ genreDescription, books }) => {
            genreBooksData[genreDescription] = books;
          });

          // Update state with fetched books
          setRecommendationsBooks(genreBooksData);
          setLoadingStates((prevLoading) => genrePreferences.reduce((acc, { Genre }) => {
            acc[Genre.description] = false;
            return acc;
          }, { ...prevLoading }));
        } catch (error) {
          // Errors handled in fetchBooksForGenre()
        }
      };

      fetchGenreBooks();
    }

    return () => controller.abort(); // Cleanup function

  }, [displayRecommendations, genrePreferences]);

  // Fetch random genres
  useEffect(() => {
    const controller = new AbortController(); // Create an AbortController

    const fetchGenres = async () => {
      try {
        setGenreError(null);
        const excludedGenreIds = genrePreferences.map((preference) => preference.Genre.id);
    
        const response = await axios.get(`${API_BASE_URL}/genres/random`, {
          params: {
            limit: genrePreferences ? 4 - genrePreferences.length : 4,
            exclude: excludedGenreIds.join(','),
          },
          signal: controller.signal, // Pass AbortController signal
        });

        setRandomGenres(response.data);
        setPreferenceFlag(false);
      } catch (error) {
        if (error.name !== 'CanceledError') {
          setGenreError("Failed to load random genres. Please try again later.");
        }
      }
    };

    if(preferenceFlag){
      fetchGenres();
    }

    return () => controller.abort();

  }, [preferenceFlag, genrePreferences]);


  return (
    <div className="home-page">

      {recommendationsError && <p className="error">{recommendationsError}</p>}

      {/* Recommendations Section */}
      {displayRecommendations && !recommendationsError && genrePreferences.map(({ Genre }) => (
        <div className="list-preview-container" key={Genre.id}>
          <div className="list-preview-header">
            <h2>{Genre.description}</h2>
            <button className="link-button" onClick={handleListViewClick(Genre.description)}>
              View All
            </button>
          </div>
          <div className="list-preview">
            {loadingStates[Genre.description] ? (
              <p>Loading {Genre.description} books...</p>
            ) : loadingStates[Genre.description] === false && errorMessages[Genre.description] ? (
              <p className="error">{errorMessages[Genre.description]}</p>
            ) : recommendationsBooks[Genre.description]?.length > 0 ? (
              recommendationsBooks[Genre.description].map((book, index) => (
                <BookDisplay key={index} book={book} onBookClick={handleBookClick} />
              ))
            ) : (
              <p>No books available for {Genre.description}</p>
            )}
          </div>
        </div>
      ))}

      
      {genreError && <p className="error">{genreError}</p>}
    
      {/* Random Genre Section */}
      {randomGenres.map((genre) => (
        <div className="list-preview-container" key={genre.id}>
          <div className="list-preview-header">
            <h2>{genre.description}</h2>
            <button className="link-button" onClick={handleListViewClick(genre.description)}>
              View All
            </button>
          </div>
          <div className="list-preview">
            {loadingStates[genre.description] ? (
              <p>Loading {genre.description} books...</p>
            ) : loadingStates[genre.description] === false && errorMessages[genre.description] ? (
              <p className="error">{errorMessages[genre.description]}</p>
            ) : genreBooks[genre.description]?.length > 0 ? (
              genreBooks[genre.description].map((book, index) => (
                <BookDisplay key={index} book={book} onBookClick={handleBookClick} />
              ))
            ) : (
              <p>No books available for {genre.description}</p>
            )}
          </div>
        </div>
      ))}


      {/* Book Details Modal */}
      {displayBookDetailsModal && (
        <BookDetailsModal
          setDisplayBookDetailsModal={setDisplayBookDetailsModal}
          bookId={selectedBookId}
        />
      )}
    </div>
  );
}

export default HomePage;